/**
 * The change case behind the panel: what it is, and every action on it.
 *
 * @module components/governance/useChangeCaseGovernance
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { changeCaseKindNoun } from "@/components/shared/governance-labels";
import { useConfirm } from "@/components/ui";
import type { EditorProgrammeMetadata, ProgrammePlatformClient, SessionContext } from "@/platform";
import type { ChangeCaseRevisionComparison } from "@/server/governance-diff";
import { blockersForGate } from "@/server/readiness-ladder";
import type { CurriculumChangeCase, ReadinessResult } from "@/server/types";
import { begin } from "@/utils/begin";
import { uid } from "@/utils/uid";

export interface ChangeCaseGovernanceInputs {
  client: ProgrammePlatformClient;
  session: SessionContext;
  programme: EditorProgrammeMetadata;
  changeCaseId: string;
  onChanged: () => Promise<void>;
  onReplacementCreated: (programmeId: string) => Promise<void>;
}

/** Why the approval cannot be recorded yet, or nothing when it can. */
function approvalRefusal(
  pending: boolean,
  changeCase: CurriculumChangeCase,
  entered: { effectiveFrom: string },
  identityChanged: boolean,
): string | undefined {
  if (pending) {
    return "Wait for the approval to be recorded.";
  }
  if (identityChanged && !changeCase.identityImpactDecision) {
    return "Record the identity decision first.";
  }
  return entered.effectiveFrom || changeCase.targetEffectiveFrom
    ? undefined
    : "Set the effective date first.";
}

export function useChangeCaseGovernance({
  client,
  session,
  programme,
  changeCaseId,
  onChanged,
  onReplacementCreated,
}: ChangeCaseGovernanceInputs) {
  const confirm = useConfirm();
  const [changeCase, setChangeCase] = useState<CurriculumChangeCase | null>(null);
  const [comparison, setComparison] = useState<ChangeCaseRevisionComparison | null>(null);
  const [readiness, setReadiness] = useState<ReadinessResult | null>(null);
  const [reviewerMissing, setReviewerMissing] = useState(false);
  const [exportLocked, setExportLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingActions, setPendingActions] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<Error | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [identityOutcome, setIdentityOutcome] = useState<
    "continue-same-programme" | "create-replacement-programme"
  >("continue-same-programme");
  const [identityRationale, setIdentityRationale] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [nextRevalidationDue, setNextRevalidationDue] = useState("");
  const [replacementProgrammeCode, setReplacementProgrammeCode] = useState("");

  // Fetch and publish, with no synchronous prologue and every update in a
  // continuation, so the mount effect below can call it without cascading a
  // second render before the first has painted. `loading` already starts true
  // and `error` null, so the mount path needs no prologue.
  const fetchChangeCase = useCallback(
    (): Promise<void> =>
      begin(async () => {
        const governance = await client.getProgrammeGovernance(programme.id);
        const current = governance.curriculumChangeCases.find((entry) => entry.id === changeCaseId);
        if (!current) {
          throw new Error("This programme change is unavailable.");
        }
        const revision = governance.curriculumRevisions.find(
          (entry) => entry.changeCaseId === changeCaseId,
        );
        return {
          current,
          // The final export snapshotted this change's revision, which nothing undoes.
          exportLocked: governance.snapshots.some(
            (snapshot) => snapshot.revisionId === revision?.id,
          ),
          reviewerMissing: governance.activeReviewerUserIds.length === 0,
          readiness:
            governance.changeCaseReadiness.find((entry) => entry.changeCaseId === changeCaseId)
              ?.readiness ?? null,
          comparison: current.submittedGovernanceRevisionId
            ? await client.compareChangeCaseRevision(programme.id, current.id)
            : null,
        };
      })
        .then(
          ({
            current,
            exportLocked: locked,
            readiness: nextReadiness,
            reviewerMissing: missing,
            comparison: nextComparison,
          }) => {
            setChangeCase(current);
            setExportLocked(locked);
            setReadiness(nextReadiness);
            setReviewerMissing(missing);
            setComparison(nextComparison);
            setError(null);
          },
        )
        .catch((cause: unknown) => {
          setError(
            cause instanceof Error ? cause : new Error("Unable to load programme change workflow"),
          );
        })
        .finally(() => {
          setLoading(false);
        }),
    [changeCaseId, client, programme.id],
  );

  // The reload an action wants: show the spinner and drop the stale error
  // straight away, then fetch.
  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    await fetchChangeCase();
  }, [fetchChangeCase]);

  useEffect(() => {
    fetchChangeCase();
  }, [fetchChangeCase]);

  /**
   * Whether one named action is in flight.
   *
   * Keyed rather than shared: two of these controls sit side by side and are
   * pressed in sequence, and a single flag left the second disabled until the
   * first request came back, which is a wait no state of the change case
   * justifies.
   */
  const isPending = (key: string): boolean => pendingActions.has(key);

  /** Whether any action is in flight, which is what the form fields wait on. */
  const busy = pendingActions.size > 0;

  const startPending = (key: string): void => {
    setPendingActions((current) => new Set(current).add(key));
  };

  const endPending = (key: string): void => {
    setPendingActions((current) => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  // Reviewing and marking reviewed are pressed in sequence on one change case,
  // and the second is only valid once the first has landed. Per-action pending
  // keeps both buttons live, so the ordering is kept here instead of by
  // disabling the next control.
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());

  /** Whether it landed, since the note a caller consumed is cleared only then. */
  const run = async (key: string, action: () => Promise<unknown>): Promise<boolean> => {
    startPending(key);
    setError(null);
    const previous = queueRef.current;
    const work = (async () => {
      await previous.catch(() => undefined);
      await action();
      await onChanged();
      await refresh();
    })();
    queueRef.current = work;
    try {
      await work;
      return true;
    } catch (cause) {
      // A refused action usually means the change moved on, and every retry
      // from the stale revision is refused the same way. Re-read it first, so
      // the next attempt carries the current revision, and report afterwards
      // so the reload cannot clear the failure.
      await Promise.resolve(onChanged()).catch(() => undefined);
      await refresh().catch(() => undefined);
      setError(cause instanceof Error ? cause : new Error("Programme change action failed"));
      return false;
    } finally {
      endPending(key);
    }
  };

  const createReplacement = async (): Promise<void> => {
    if (!changeCase || !replacementProgrammeCode.trim()) {
      return;
    }
    const confirmed = await confirm({
      title: "Create a replacement programme?",
      body: `The reviewed content becomes a new programme, ${replacementProgrammeCode.trim()}, linked to this one. This cannot be undone.`,
      confirmLabel: "Create replacement programme",
      "data-testid": "replacement-programme-confirmation",
    });
    if (!confirmed) {
      return;
    }
    startPending("create-replacement");
    setError(null);
    try {
      const result = await client.createReplacementProgramme({
        programmeId: programme.id,
        changeCaseId: changeCase.id,
        replacementProgrammeId: uid("programme"),
        programmeCode: replacementProgrammeCode.trim(),
      });
      await onReplacementCreated(result.programme.id);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause : new Error("Unable to create replacement programme"),
      );
    } finally {
      endPending("create-replacement");
    }
  };

  /** Approval makes the change case an immutable revision, so it asks first. */
  const approve = async (): Promise<void> => {
    if (!changeCase) {
      return;
    }
    const confirmed = await confirm({
      title: `Approve this ${changeCaseKindNoun(changeCase.kind)}?`,
      body: "It becomes an approved curriculum revision, which cannot be changed afterwards.",
      confirmLabel: `Approve ${changeCaseKindNoun(changeCase.kind)}`,
      "data-testid": "change-case-approval-confirmation",
    });
    if (!confirmed) {
      return;
    }
    await run("approve", () =>
      client.approveChangeCase({
        programmeId: programme.id,
        changeCaseId: changeCase.id,
        ...(effectiveFrom ? { effectiveFrom } : {}),
        ...(nextRevalidationDue ? { nextRevalidationDue } : {}),
      }),
    );
  };

  const isLead = session.subject.responsibilities.some(
    (entry) => entry.programmeId === programme.id && entry.responsibility === "lead",
  );
  const isReviewer = session.subject.responsibilities.some(
    (entry) => entry.programmeId === programme.id && entry.responsibility === "reviewer",
  );
  const isClientAdmin = session.subject.accountRole === "client-admin";
  const identityChanges = comparison?.identityChanges ?? [];
  // What the submission gate waits on; later rungs are for approval.
  const submissionBlockers = readiness
    ? blockersForGate(readiness.blockers, "Ready for internal review")
    : null;
  const approveRefusal = changeCase
    ? approvalRefusal(
        isPending("approve"),
        changeCase,
        { effectiveFrom },
        identityChanges.length > 0,
      )
    : undefined;
  // Before submission the route follows the current team; after, the submitted one.
  const unsent = ["team-confirmation", "open", "changes-required"].includes(
    changeCase?.status ?? "open",
  );
  const toQa = unsent ? reviewerMissing : changeCase?.internalReviewRequired === false;

  return {
    changeCase,
    comparison,
    loading,
    error,
    setError,
    reviewNote,
    setReviewNote,
    identityOutcome,
    setIdentityOutcome,
    identityRationale,
    setIdentityRationale,
    effectiveFrom,
    setEffectiveFrom,
    nextRevalidationDue,
    setNextRevalidationDue,
    replacementProgrammeCode,
    setReplacementProgrammeCode,
    isPending,
    busy,
    run,
    refresh,
    createReplacement,
    approve,
    isLead,
    isReviewer,
    isClientAdmin,
    identityChanges,
    approveRefusal,
    submissionBlockers,
    reviewerMissing,
    exportLocked,
    toQa,
  };
}
